import { motion } from 'framer-motion'
import Link from './Link'
import { products, type Product } from '../data/products'
import { getProductSeoContent } from '../data/seo-content'

type ProductPageProps = {
  product: Product
}

const ProductPage = ({ product }: ProductPageProps) => {
  const seoContent = getProductSeoContent(product.slug)
  const related = products.filter(
    (item) => item.categorySlug === product.categorySlug && item.id !== product.id
  )
  const fallbackRelated = products.filter((item) => item.id !== product.id).slice(0, 3)
  const suggestions = related.length > 0 ? related : fallbackRelated

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    sku: product.code,
    description: product.summary,
    image: [`https://www.angeltechpo.com.cn${product.image}`],
    category: product.category,
    brand: { '@type': 'Brand', name: 'Angel Technology' },
    manufacturer: { '@type': 'Organization', name: 'Angel Technology International Co., Ltd.' }
  }

  const faqJsonLd = seoContent
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: seoContent.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer }
        }))
      }
    : undefined

  return (
    <main className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}

      <section className="bg-slate-950 pb-12 pt-32 text-white">
        <div className="container mx-auto px-4">
          <nav aria-label="Breadcrumb" className="text-sm text-slate-400">
            <Link to="/" className="transition-colors hover:text-white">
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link to={`/category/${product.categorySlug}`} className="transition-colors hover:text-white">
              {product.category}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-slate-200">{product.name}</span>
          </nav>
          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.24em] text-teal-300">
            {product.code}
          </p>
          <h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-tight md:text-5xl">
            {product.name}
          </h1>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid gap-12 lg:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-8">
              <img
                src={product.image}
                alt={product.alt}
                width={620}
                height={620}
                className="mx-auto w-full max-w-[420px] object-contain"
              />
            </div>

            <div>
              <p className="text-lg leading-8 text-slate-700">{product.summary}</p>

              <h2 className="mt-10 text-2xl font-semibold text-slate-950">Key features</h2>
              <ul className="mt-4 space-y-3">
                {product.details.map((detail) => (
                  <li key={detail} className="flex gap-3 text-base leading-7 text-slate-600">
                    <span className="mt-2.5 h-1.5 w-1.5 flex-none rounded-full bg-teal-600" />
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>

              <h2 className="mt-10 text-2xl font-semibold text-slate-950">Specifications</h2>
              <dl className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
                <div className="flex justify-between gap-6 py-3">
                  <dt className="text-sm text-slate-500">Product code</dt>
                  <dd className="text-sm font-semibold text-slate-900">{product.code}</dd>
                </div>
                <div className="flex justify-between gap-6 py-3">
                  <dt className="text-sm text-slate-500">Category</dt>
                  <dd className="text-sm font-semibold text-slate-900">
                    <Link
                      to={`/category/${product.categorySlug}`}
                      className="transition-colors hover:text-teal-700"
                    >
                      {product.category}
                    </Link>
                  </dd>
                </div>
                <div className="flex justify-between gap-6 py-3">
                  <dt className="text-sm text-slate-500">Brand</dt>
                  <dd className="text-sm font-semibold text-slate-900">Angel Technology</dd>
                </div>
              </dl>

              <h2 className="mt-10 text-2xl font-semibold text-slate-950">Ordering and supply</h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                Available for wholesale and OEM supply to distributors, prosthetic clinics and
                rehabilitation centres. Quantity pricing, mixed containers and documentation support
                are available on request. Tell us the product code and target quantity and we will
                come back with lead time and shipping options.
              </p>

              <Link
                to="/#contact"
                className="mt-6 inline-block rounded-md bg-slate-950 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-700"
              >
                Request a quote
              </Link>
            </div>
          </div>

          {seoContent && (
            <section className="mt-20 border-t border-slate-200 pt-12">
              <div className="grid gap-10 lg:grid-cols-2">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-teal-700">
                    Selection support
                  </p>
                  <h2 className="mt-3 text-3xl font-semibold text-slate-950">
                    Applications and fitting context
                  </h2>
                  <ul className="mt-6 space-y-4">
                    {seoContent.applications.map((application) => (
                      <li key={application} className="flex gap-3 text-base leading-7 text-slate-600">
                        <span className="mt-2.5 h-1.5 w-1.5 flex-none rounded-full bg-teal-600" />
                        <span>{application}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg bg-slate-50 p-7 ring-1 ring-slate-200">
                  <h2 className="text-2xl font-semibold text-slate-950">What buyers should confirm</h2>
                  <ol className="mt-5 space-y-4">
                    {seoContent.buyerConsiderations.map((item, index) => (
                      <li key={item} className="flex gap-4 text-sm leading-7 text-slate-600">
                        <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-slate-950 text-xs font-semibold text-white">
                          {index + 1}
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              <div className="mt-14">
                <h2 className="text-3xl font-semibold text-slate-950">Buyer questions</h2>
                <div className="mt-6 grid gap-5 md:grid-cols-2">
                  {seoContent.faq.map((item) => (
                    <article key={item.question} className="rounded-lg border border-slate-200 p-6">
                      <h3 className="text-lg font-semibold text-slate-950">{item.question}</h3>
                      <p className="mt-3 text-sm leading-7 text-slate-600">{item.answer}</p>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="mt-20 border-t border-slate-200 pt-12">
            <h2 className="text-2xl font-semibold text-slate-950">Related products</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {suggestions.slice(0, 3).map((item, index) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: index * 0.05 }}
                  className="group overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
                >
                  <Link
                    to={`/product/${item.slug}`}
                    className="block bg-gradient-to-b from-white to-slate-50 p-6"
                  >
                    <img
                      src={item.image}
                      alt={item.alt}
                      loading="lazy"
                      width={310}
                      height={310}
                      className="mx-auto aspect-square w-full max-w-[260px] object-contain"
                    />
                  </Link>
                  <div className="border-t border-slate-100 p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      {item.code}
                    </p>
                    <h3 className="mt-2 text-lg font-semibold text-slate-950">
                      <Link to={`/product/${item.slug}`} className="transition-colors hover:text-teal-700">
                        {item.name}
                      </Link>
                    </h3>
                  </div>
                </motion.article>
              ))}
            </div>
            <p className="mt-8 text-sm">
              <Link to={`/category/${product.categorySlug}`} className="font-semibold text-teal-700 hover:text-teal-800">
                View all {product.category} products →
              </Link>
            </p>
          </section>
        </div>
      </section>
    </main>
  )
}

export default ProductPage
